import { Button, GenericCombobox, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { NON_EU_COUNTRIES } from '@/common'
import { SupplierFormData, supplierAddressSchema } from '../SupplierForm.schema'
import z from 'zod'
import { useMemo, useState } from 'react'
import { orderBy } from 'es-toolkit'

type StateSchema = z.infer<typeof supplierAddressSchema>

type Props = {
  data: SupplierFormData
  onNextClick: (data: StateSchema) => void
  onBackClick: () => void
}

export default function SupplierAddressDetailsStep({ data, onNextClick, onBackClick }: Props) {
  const { t } = useI18n()

  const [state, setState] = useState<StateSchema>({
    street: data.street,
    streetNumber: data.streetNumber,
    street2: data.street2,
    postCode: data.postCode,
    city: data.city,
    poBox: data.poBox,
    subDivision: data.subDivision,
    country: data.country
  })

  const { street, streetNumber, street2, postCode, city, poBox, subDivision, country } = state

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  const handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setState(prev => ({ ...prev, [evt.target.id]: evt.target.value }))
  }

  const handleCountryChange = (value: { value: string; label: string } | null) => {
    setState(prev => ({ ...prev, country: value?.value || '' }))
  }

  const validation = supplierAddressSchema.safeParse(state)
  const fieldsErrorDisplayMap = useMemo(() => {
    const fieldErrors = validation.error ? z.flattenError(validation.error).fieldErrors : {}
    function getError(field: string) {
      return fieldErrors[field as keyof typeof fieldErrors]?.join(', ') ?? undefined
    }
    return {
      street: getError('street'),
      streetNumber: getError('streetNumber'),
      postCode: getError('postCode'),
      city: getError('city'),
      poBox: getError('poBox'),
      subDivision: getError('subDivision'),
      street2: getError('street2')
    }
  }, [validation])

  const handleNext = () => {
    onNextClick(state)
  }

  return (
    <div className="space-y-6 pb-2">
      <h2 className="text-xl font-semibold mb-2">
        {t('suppliers:supplierInputFields.sections.address')}
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        {t('suppliers:createSupplierDialog.steps.descriptions.address')}
      </p>
      <div className=" space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            error={fieldsErrorDisplayMap.street}
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={handleInputChange}
          />

          <InputGroup
            id="streetNumber"
            error={fieldsErrorDisplayMap.streetNumber}
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={handleInputChange}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={handleInputChange}
          error={fieldsErrorDisplayMap.street2}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={handleInputChange}
            required={
              NON_EU_COUNTRIES.find(countryItem => countryItem.value === country)
                ?.isPostCodeRequired
            }
            error={fieldsErrorDisplayMap.postCode}
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            onChange={handleInputChange}
            required
            error={fieldsErrorDisplayMap.city}
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={handleInputChange}
            error={fieldsErrorDisplayMap.poBox}
          />
        </div>
        <InputGroup
          id="subDivision"
          label={t('common:inputFields.subDivision.label')}
          value={subDivision}
          onChange={handleInputChange}
          error={fieldsErrorDisplayMap.subDivision}
        />
        <GenericCombobox
          required
          label={t('common:inputFields.country.label')}
          items={countryOptions}
          value={
            country ? { value: country, label: t(`common:countriesByIsoAlpha2:${country}`) } : null
          }
          onChange={handleCountryChange}
        />
      </div>
      <div className="flex justify-between pt-16">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.back')}
        </Button>
        <Button onClick={handleNext} disabled={!validation.success}>
          {t('common:actions.next')}
        </Button>
      </div>
    </div>
  )
}
